import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { filter, Subject, takeUntil } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { Navigation, SwiperOptions } from 'swiper';
import { IToolbarData } from 'src/app/shared/models/components-data.models';
import { ItemStatus } from 'src/app/shared/models/item.models';
import { successSecondModalData } from '../second-hand';
import { SecondHandStore } from '../second-hand-store';
import { environment } from 'src/environments/environment';
import { UserService } from 'src/app/auth/user.service';
import { IUser } from 'src/app/auth/auth.models';
import { ReactionsService } from 'src/app/services/reactions.service';
import { CommentsService } from 'src/app/services/comments.service';
import { IReaction } from 'src/app/activity-feed/activity-feed';
import { PopoverController, ToastController } from '@ionic/angular';
import { ShareOptionsComponent } from '../../../activity-feed/post-details/share-options/share-options.component';

const TOOLBAR_DATA: IToolbarData = {
  arrowRouter: '/bazaar/second-hand',
  btnSlot: 'end',
  title: 'Second Hand',
};

const TOOLBAR_SWIPER: IToolbarData = {
  arrow: true,
  btnSlot: 'end',
  title: 'Second Hand',
};

@Component({
  selector: 'app-second-hand-details',
  templateUrl: './second-hand-details.component.html',
  styleUrls: ['./second-hand-details.component.scss'],
})
export class SecondHandDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('col') col: ElementRef;

  TOOLBAR_DATA = TOOLBAR_DATA;
  TOOLBAR_SWIPER = TOOLBAR_SWIPER;
  config: SwiperOptions;

  secondHandItem;
  galleryUrls: string[];

  currentUserID;
  creator;
  currentUser: IUser;
  isAdmin: boolean;

  itemImage: string;
  status: ItemStatus;

  showStatusModal: boolean = false;
  itemStatus;

  flagData;
  isContentFlaggedModal;
  isOpenFlagModal;

  isOpen = false;
  showSuccessModal = false;
  successModalData;
  isOpenFormModal = false;

  // Tab control properties
  selectedTab: string = 'details'; // Default to 'details' tab
  isTabLoading: boolean = false; // Add loading state for tabs
  isCommentsLoaded: boolean = false; // Track if comments are already loaded

  // Social actions properties
  likesCount: number = 0;
  commentsCount: number = 0;
  hasLiked: boolean = false;
  isUserFollowed: boolean = false;

  discordServerInviteLink = environment.discordServerInviteLink;
  itemId: string;
  private destroy$ = new Subject<void>();

  constructor(
    private secondHandStore: SecondHandStore,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private reactionsService: ReactionsService,
    private commentsService: CommentsService,
    private popoverController: PopoverController,
    private toastController: ToastController
  ) {
    this.itemId = this.activatedRoute.snapshot.params.id;
  }

  ngOnInit() {
    this.getUser();
    const app: HTMLElement = document.querySelector('ion-router-outlet');
    app.style.marginBottom = '0px';

    this.secondHandStore.getItemByByValue({
      documentId: this.activatedRoute.snapshot.params.id,
    });

    // Check if there's a tab query parameter
    const tabParam = this.activatedRoute.snapshot.queryParamMap.get('tab');
    if (tabParam) {
      this.selectedTab = tabParam;
    }

    this.listenSecondHandDetails();

    this.secondHandStore.selectShowFormModal().subscribe(isOpen => {
      this.isOpenFormModal = isOpen;
    });

    this.secondHandStore.selectShowPendingModal().subscribe(isOpen => {
      this.showSuccessModal = isOpen;
    });
    this.successModalData = successSecondModalData;

    // Load social interactions data
    this.loadSocialData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSocialData() {
    // Get likes for this item
    if (this.itemId) {
      this.reactionsService
        .getReactions()
        .pipe(
          takeUntil(this.destroy$),
          map(
            reactions =>
              reactions.filter(r => r.id === this.itemId) as IReaction[]
          )
        )
        .subscribe(reactions => {
          const itemReaction = reactions.find(r => r.id === this.itemId);
          const likes = itemReaction?.likes || [];
          this.likesCount = likes.length || 0;
          this.hasLiked = this.currentUserID
            ? likes.includes(this.currentUserID)
            : false;
        });

      this.commentsService
        .getCommentCount(this.itemId, 'secondHand')
        .pipe(takeUntil(this.destroy$))
        .subscribe(count => {
          this.commentsCount = count || 0;
        });
    }
  }

  handleOk(e) {
    e ? this.secondHandStore.updateShowPendingModal(false) : null;
  }

  handleCancel(e) {
    e ? this.secondHandStore.updateShowPendingModal(false) : null;
  }

  openModal() {
    this.secondHandStore.updateShowFormModal(true);
  }

  closeModal(e) {
    e ? this.secondHandStore.updateShowFormModal(false) : null;
  }

  toggleGallery(i?) {
    this.isOpen = !this.isOpen;
    this.config = {
      modules: [Navigation],
      loop: true,
      navigation: true,
      initialSlide: i,
    };
  }

  async flagContent(slide) {
    slide.type = 'secondHand';

    this.flagData = slide;

    const functions = getFunctions();
    const isContentFlagged = httpsCallable(functions, 'isContentFlagged');
    const res = await isContentFlagged({
      itemId: slide.id,
      url: slide.images[0],
      type: this.flagData.type,
    });
    if (res.data === true) {
      this.isContentFlaggedModal = true;
    } else {
      this.isOpenFlagModal = true;
    }
  }

  openStatusModal(status?) {
    this.showStatusModal = !this.showStatusModal;
    if (status) this.itemStatus = status;
  }

  // Handle tab changes with loading effect
  onTabChange(event: any) {
    const newTab = event.detail.value;

    if (this.selectedTab === newTab) {
      return; // Skip if same tab is selected
    }

    // Simulate loading state but keep header visible
    this.isTabLoading = true;

    // Maintain scroll position at the top to keep header visible
    const cardElement = document.getElementById('cardDetail');
    if (cardElement) {
      cardElement.scrollTop = 0;
    }

    // If switching to comments and they haven't been loaded yet
    if (newTab === 'comments' && !this.isCommentsLoaded) {
      setTimeout(() => {
        this.selectedTab = newTab;
        this.isTabLoading = false;
        this.isCommentsLoaded = true;
      }, 300); // Short timeout for a smooth effect
    } else {
      // For already loaded tabs or Details tab
      setTimeout(() => {
        this.selectedTab = newTab;
        this.isTabLoading = false;
      }, 150); // Shorter timeout for Already loaded content
    }

    // Preload comments tab data if we're on details tab
    // This ensures comments are ready when user switches tabs
    if (this.selectedTab === 'details' && !this.isCommentsLoaded) {
      // Set a timer to preload comments data after a short delay
      setTimeout(() => {
        this.isCommentsLoaded = true;
      }, 1000); // Delay preloading to prevent performance issues
    }
  }

  // Function to open comments tab directly
  goToComments() {
    this.selectedTab = 'comments';
    this.onTabChange({ detail: { value: 'comments' } });
  }

  private listenSecondHandDetails() {
    this.secondHandStore
      .selectItem()
      .pipe(
        takeUntil(this.destroy$),
        filter(item => !!item),
        switchMap(item => {
          this.secondHandItem = item;
          this.itemImage = item.images[0];
          return this.userService.getUserByUid(item.creator.id);
        }),
        filter(user => !!user)
      )
      .subscribe(user => {
        this.creator = user;
        this.galleryUrls = this.secondHandItem.images.map(
          pic => `url("${pic}") no-repeat center center / cover`
        );
      });
  }

  private getUser() {
    this.userService.currentUser$
      .pipe(
        takeUntil(this.destroy$),
        filter(user => !!user.uid)
      )
      .subscribe(user => {
        this.currentUser = user;
        this.currentUserID = user.uid;
        this.isAdmin = this.userService.isAdmin;
      });
  }

  async shareItem(event: any) {
    const postUrl = `${window.location.origin}/bazaar/second-hand/${this.itemId}`;
    const postTitle = this.secondHandItem?.name || 'MerMapp Second-hand Item';
    const postText =
      this.secondHandItem?.description ||
      'Check out this second-hand item on MerMapp!';

    const popover = await this.popoverController.create({
      component: ShareOptionsComponent,
      componentProps: {
        postUrl: postUrl,
        postTitle: postTitle,
        postText: postText,
        onOptionSelected: (option: string) => {
          this.handleShareOption(option, postUrl, postTitle, postText);
          popover.dismiss();
        },
      },
      event: event,
      translucent: true,
      backdropDismiss: true,
      alignment: 'center',
      side: 'top',
      arrow: true,
    });

    await popover.present();
  }

  private async handleShareOption(
    option: string,
    postUrl: string,
    postTitle: string,
    postText: string
  ) {
    switch (option) {
      case 'copy':
        await this.copyToClipboard(postUrl);
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            postUrl
          )}`,
          '_blank'
        );
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            postUrl
          )}&text=${encodeURIComponent(postText)}`,
          '_blank'
        );
        break;
      case 'whatsapp':
        window.open(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(
            `${postText} ${postUrl}`
          )}`,
          '_blank'
        );
        break;
      case 'email':
        window.open(
          `mailto:?subject=${encodeURIComponent(
            postTitle
          )}&body=${encodeURIComponent(`${postText} ${postUrl}`)}`,
          '_blank'
        );
        break;
      case 'native':
        await this.useNativeShare(postUrl, postTitle, postText);
        break;
    }
  }

  private async copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      const toast = await this.toastController.create({
        message: 'Link copied to clipboard!',
        duration: 2000,
        position: 'bottom',
      });
      await toast.present();
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }

  private async useNativeShare(postUrl: string, title: string, text: string) {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: text,
          url: postUrl,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      const toast = await this.toastController.create({
        message: 'Share functionality not available on this device',
        duration: 2000,
        position: 'bottom',
      });
      await toast.present();
    }
  }

  openChat() {
    if (this.creator?.id) {
      this.router.navigate(['/chat/detail', this.creator.id]);
    }
  }

  putLikeOrDislike() {
    if (!this.currentUserID) return;

    try {
      this.reactionsService.putLikeOrDislike(this.itemId, 'secondHand');

      // Optimistic UI update
      this.hasLiked = !this.hasLiked;
      this.likesCount = this.hasLiked
        ? this.likesCount + 1
        : this.likesCount - 1;
    } catch (error) {
      console.error('Error liking/disliking item:', error);
    }
  }
}

